import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseDirective } from '@egym/ngx-adm-int';
import { ChangeLogService } from '../../service/changelog.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeLog,
  ChangeLogFilter,
  ChangeLogFilterAndPagination,
  ChangeReviewStatus,
  ChangeSource,
  FIELD_TYPE_OPTIONS,
  GymChangeLogResponse,
} from '../../model/changelog';
import { MatPaginator } from '@angular/material/paginator';
import { Subscription, tap } from 'rxjs';
import { ChangelogUtil } from '../../util/changelog-util';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ChangelogDetailsComponent } from '../../components/changelog-details/changelog-details.component';
import { NetworkType } from '../../model/gym';

const NOT_SET_NETWORK_FILTER = 'NOT_SET';

@Component({
  selector: 'gymfinder-changelog',
  templateUrl: './changelog.component.html',
  styleUrls: ['./changelog.component.scss'],
  standalone: false,
})
export class ChangelogComponent extends BaseDirective<void> implements OnInit {
  displayedColumns: string[] = [
    'gymId',
    'gymName',
    'event',
    'when',
    'reviewStatus',
    'who',
    'changeSource',
    'network',
  ];
  changeLogs: ChangeLog[] = [];

  paginationTokens: string[] = [];

  selectedReviewStatus: ChangeReviewStatus[] = [ChangeReviewStatus.NOT_VIEWED];
  selectedSource: null | ChangeSource = ChangeSource.PARTNER_PORTAL;
  selectedNetwork: string[] = [];
  selectedFieldTypes: string[] = [];

  egymGymId: null | string;

  changeSources = [
    { value: ChangeSource.PARTNER_PORTAL, display: 'Business Suite' },
    { value: ChangeSource.INTERNAL_ADMIN, display: 'Internal Admin' },
  ];

  reviewStatuses = [
    { value: ChangeReviewStatus.VIEWED, display: 'Viewed' },
    { value: ChangeReviewStatus.NOT_VIEWED, display: 'Not Viewed' },
    {
      value: ChangeReviewStatus.NO_REVIEW_REQUIRED,
      display: 'No review needed',
    },
  ];

  networkFilterOptions = [
    ...Object.values(NetworkType).map(network => ({
      value: network,
      display: ChangelogUtil.getDisplayNetwork(network),
    })),
    { value: NOT_SET_NETWORK_FILTER, display: 'Not set' },
  ];

  fieldTypeOptions = FIELD_TYPE_OPTIONS;

  totalCount = 0;

  defaultLimit = 10;

  loading = false;

  private changeLogsSubscription?: Subscription;
  private totalCountSubscription?: Subscription;

  constructor(
    snackBar: MatSnackBar,
    private readonly changeLogService: ChangeLogService,
    private readonly dialog: MatDialog
  ) {
    super(snackBar);
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;

  ngOnInit(): void {
    this.loading = true;
    this.paginator.page.pipe(tap(() => this.getChangeLogs())).subscribe();
    this.getChangeLogs();
    this.getTotalCount();
  }

  onReviewStatusChanged(event: MatOptionSelectionChange<ChangeReviewStatus>) {
    if (!event.isUserInput) {
      return;
    }
    const status = event.source?.value;
    if (event.source?.selected) {
      this.selectedReviewStatus?.push(event.source?.value);
    } else {
      const index = this.selectedReviewStatus.indexOf(status);
      this.selectedReviewStatus.splice(index, 1);
    }

    this.resetPagination();
    this.getChangeLogs();
    this.getTotalCount();
  }

  onNetworkFilterChanged() {
    this.resetPagination();
    this.getChangeLogs();
    this.getTotalCount();
  }

  onFieldTypeChanged(event: MatOptionSelectionChange<string>) {
    if (!event.isUserInput) return;
    const fieldType = event.source?.value;
    if (!fieldType) return;
    if (event.source.selected) {
      this.selectedFieldTypes.push(fieldType);
    } else {
      const index = this.selectedFieldTypes.indexOf(fieldType);
      if (index !== -1) {
        this.selectedFieldTypes.splice(index, 1);
      }
    }
    this.resetPagination();
    this.getChangeLogs();
    this.getTotalCount();
  }

  resetPagination() {
    this.paginator.pageIndex = 0;
    this.paginationTokens = [];
  }

  clearFilters() {
    this.selectedReviewStatus = [];
    this.selectedSource = null;
    this.selectedNetwork = [];
    this.selectedFieldTypes = [];
    this.egymGymId = null;
    this.resetPagination();
    this.getChangeLogs();
    this.getTotalCount();
  }

  setGymIdFilter(egymGymId: string) {
    this.egymGymId = egymGymId;
    this.resetPagination();
    this.getChangeLogs();
    this.getTotalCount();
  }

  onSourceChanged(event: MatOptionSelectionChange<ChangeSource>) {
    if (!event.isUserInput) {
      return;
    }
    this.selectedSource = event.source?.value;
    this.selectedReviewStatus = [];
    this.resetPagination();
    this.getChangeLogs();
    this.getTotalCount();
  }

  isStatusDisabled(status: ChangeReviewStatus, source: ChangeSource | null) {
    switch (source) {
      case ChangeSource.INTERNAL_ADMIN:
        return status !== ChangeReviewStatus.NO_REVIEW_REQUIRED;
      case ChangeSource.PARTNER_PORTAL:
        return status === ChangeReviewStatus.NO_REVIEW_REQUIRED;
      default:
        return false;
    }
  }

  showTooltip(value: string) {
    return value.length >= 30;
  }

  greyOutSource(source: ChangeSource) {
    return source === ChangeSource.INTERNAL_ADMIN;
  }

  getChangeLogs() {
    const filter = this.getFilterAndPagination();
    this.loading = true;
    // Cancel any in-flight request so rapid filter/pagination changes don't stack up or apply
    // out-of-order responses.
    this.changeLogsSubscription?.unsubscribe();
    this.changeLogsSubscription = this.changeLogService
      .getChangeLogs(filter)
      .subscribe({
        next: (value: GymChangeLogResponse) => {
          this.changeLogs = ChangelogUtil.formatLogsToDisplay(
            value.gymChangeLogs
          );
          this.updatePaginationTokens(
            this.paginator.pageIndex,
            value.nextToken
          );
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.changeLogs = [];
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  updatePaginationTokens(pageIndex: number, nextToken?: string) {
    // Remove all tokens after the current page (when previous is clicked)
    if (pageIndex < this.paginationTokens.length) {
      this.paginationTokens = this.paginationTokens.slice(0, pageIndex);
    }
    // Add the next token to the list
    if (nextToken) {
      this.paginationTokens.push(nextToken);
    }
  }

  getTotalCount() {
    const filter = this.getFilter();
    this.totalCountSubscription?.unsubscribe();
    this.totalCountSubscription = this.changeLogService
      .getChangeLogsCount(filter)
      .subscribe({
        next: (value: number) => {
          this.totalCount = value;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
        },
      });
  }

  getFilterAndPagination(): ChangeLogFilterAndPagination {
    const pageIndex = this.paginator.pageIndex;
    const pageSize = this.paginator.pageSize || this.defaultLimit;
    const status = this.getReviewStatus();
    const selectedNetworks = this.getSelectedNetworks();

    return {
      ...(pageSize && { limit: pageSize }),
      ...(this.paginationTokens.length &&
        pageIndex > 0 && {
          nextToken: this.paginationTokens[pageIndex - 1],
        }),
      ...(status && {
        reviewStatus: status,
      }),
      ...(this.selectedSource && {
        source: this.selectedSource,
      }),
      ...(this.egymGymId && {
        egymGymId: this.egymGymId,
      }),
      ...(selectedNetworks.length && {
        network: selectedNetworks,
      }),
      ...(this.isNotSetNetworkSelected() && {
        networkNotSet: true,
      }),
      ...(this.selectedFieldTypes.length && { fieldType: this.selectedFieldTypes }),
    };
  }

  getReviewStatus() {
    if (!this.selectedReviewStatus?.length) {
      return null;
    }
    return this.selectedReviewStatus.length > 1
      ? null
      : this.selectedReviewStatus.values().next().value;
  }

  getFilter(): ChangeLogFilter {
    const status = this.getReviewStatus();
    const selectedRealNetworks = this.getSelectedNetworks();
    return {
      ...(status && {
        reviewStatus: status,
      }),
      ...(this.selectedSource && {
        source: this.selectedSource,
      }),
      ...(this.egymGymId && {
        egymGymId: this.egymGymId,
      }),
      ...(selectedRealNetworks.length && {
        network: selectedRealNetworks,
      }),
      ...(this.isNotSetNetworkSelected() && {
        networkNotSet: true,
      }),
      ...(this.selectedFieldTypes.length && { fieldType: this.selectedFieldTypes }),
    };
  }

  private getSelectedNetworks(): string[] {
    return this.selectedNetwork.filter(
      network => network !== NOT_SET_NETWORK_FILTER
    );
  }

  private isNotSetNetworkSelected(): boolean {
    return this.selectedNetwork.includes(NOT_SET_NETWORK_FILTER);
  }

  protected readonly Array = Array;

  openChangelogDialog(changelog: ChangeLog) {
    this.dialog
      .open(ChangelogDetailsComponent, {
        data: changelog,
        width: '80%',
        maxWidth: '1280px',
        maxHeight: '90%',
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.reload();
        }
      });
  }

  reload() {
    // Remove last nextToken to redo the request
    this.paginationTokens.pop();

    this.getChangeLogs();
    this.getTotalCount();
  }
}
